import { Injectable } from "@angular/core";
import { Cell, CellState, Graph } from "@maxgraph/core";
import { Identifier } from "@nms-ng2/app/shared/models/basic.models";
import {
    AnyDrawCommand,
    CellKind,
    CustomEdgeParameters,
    IGNORED_PROPERTIES_MAP
} from "@nms-ng2/app/shared/services/layout-manager/drawing.models";
import { CustomElementLayoutBuilder } from "../layout/custom-element-layout-builder.service";
import { CustomCell, CustomCellState, SourceOrTarget } from "../models/diagram.models";

/**
 * Responsável por desenhar diagramas com o MaxGraph.
 *
 * Atualmente utilizado apenas para desenhar o diagrama de circuitos DWDM.
 */
@Injectable({
    providedIn: "root"
})
export class DiagramDrawerService {

    private readonly edgeCells: CustomCell[] = [];

    /**
     * Desenha um diagrama de acordo com os comandos recebidos por parâmetro.
     *
     * @param graph referência ao gráfico do MaxGraph onde o diagrama será desenhado
     * @param commands lista ordenada de comandos a serem desenhados
     *
     * @returns um mapa com todas as células criadas
     */
    draw(graph: Graph, commands: AnyDrawCommand[]): Map<Identifier, CustomCell> {
        const cellIdentifierMap = new Map();

        graph.batchUpdate(() => {
            for (const command of commands) {
                let createdCell: CustomCell;

                switch (command.kind) {
                    case CellKind.VERTEX:
                        createdCell = graph.insertVertex(
                            this.convertToMaxGraphParameters(cellIdentifierMap, command)) as CustomCell;

                        createdCell.buildPopover = command.parameters.custom?.buildPopover;
                        createdCell.popoverData = command.parameters.custom?.popoverData;
                        createdCell.getCustomTooltip = command.parameters.custom?.getCustomTooltip;

                        break;
                    case CellKind.EDGE:
                        createdCell = graph.insertEdge(
                            this.convertToMaxGraphParameters(cellIdentifierMap, command)) as CustomCell;

                        this.handleCustomEdge(createdCell, command, graph);

                        this.edgeCells.push(createdCell);
                        break;
                }

                cellIdentifierMap.set(command.id, createdCell);
            }
        });

        this.handleAnimations(graph);

        return cellIdentifierMap;
    }

    /**
     * Limpa o modelo de dados do gráfico do MaxGraph.
     */
    clear(graph: Graph) {
        graph.getDataModel().clear();
    }

    /**
     * Define os comportamentos customizados para as arestas do diagrama (i.e. links).
     *
     * @param createdCell célula criada para a comando em questão
     * @param command comando de desenho da aresta em questão
     * @param graph referência ao gráfico do MaxGraph onde o diagrama será desenhado
     */
    private handleCustomEdge(createdCell: CustomCell, command: AnyDrawCommand, graph: Graph) {
        const edgeParameters = command.parameters as CustomEdgeParameters;

        createdCell.customName = command.name;
        createdCell.hasAnimation = edgeParameters.custom?.hasAnimation;
        createdCell.getCustomTooltip = edgeParameters.custom?.getCustomTooltip;
        createdCell.buildPopover = edgeParameters.custom?.buildPopover;
        createdCell.popoverData = edgeParameters.custom?.popoverData;

        this.setupCustomEdgeStyle(createdCell, command);

        if (createdCell.getStyle().deletable) {
            createdCell.customBuildCallback = (cellState: CellState) =>
                CustomElementLayoutBuilder.insertDeleteButton(graph, cellState, command);
        }
    }

    /**
     * Define uma função de estilo customizado para as arestas (i.e. links).
     * Atualmente utilizada basicamente para reposicionar os pontos dos links internos.
     *
     * @param createdCell célula criada para a comando em questão
     * @param command comando de desenho da aresta em questão
     */
    private setupCustomEdgeStyle(createdCell: CustomCell, command: AnyDrawCommand) {
        createdCell.setCustomStyle = (cellState: CustomCellState) => {
            if (command.parameters.custom?.repositionX) {
                const { offset, points } = command.parameters.custom.repositionX;
                CustomElementLayoutBuilder.repositionLinkXAxis(cellState, offset, points);
            }

            if (command.parameters.custom?.repositionY) {
                const { offset, points, basedOn } = command.parameters.custom.repositionY;
                const visibleState = this.getVisibleState(cellState, basedOn);
                CustomElementLayoutBuilder.repositionLinkYAxis(cellState, visibleState, offset, points);
            }
        };
    }

    /**
     * Obtém o estado do terminal de origem ou destino de acordo com o valor passado por parâmetro.
     * Por padrão, caso basedOn não exista, retorna `source`
     *
     * @param cellState estado atual da célula em questão
     * @param basedOn modelo que define se deve levar em consideração o source ou o target
     * @returns CellState | null
     */
    private getVisibleState(cellState: CustomCellState, basedOn?: SourceOrTarget) {
        return (basedOn === "target")
            ? cellState.visibleTargetState
            : cellState.visibleSourceState;
    }

    /**
     * Converte os parâmetros customizados de Vertex e Edge de acordo com o que o MaxGraph espera.
     *
     * No {@link AnyDrawCommand} os parâmetros são do tipo {@link CustomVertexParameters} ou {@link CustomEdgeParameters}.
     * Ao enviar os comandos para que o MaxGraph desenhe os elementos, é necessário antes, converter estes modelos para
     * os modelos do MaxGraph ({@link VertexParameters} e {@link EdgeParameters}).
     *
     * Isto é necessário porque obtemos a referência para uma célula apenas no resultado de `Graph.insertVertex()`
     * ou `Graph.insertEdge()`. No momento que os comandos foram gerados, estas chamadas ainda não foram feitas,
     * e como as vezes algum elementos precisam referenciar outros (e.g. edges precisam de source e target),
     * então tipos customizados foram criados para poder referenciar outros elementos a partir
     * de identificadores ao invés de células.
     *
     * O que este conversor faz é procurar no mapa de células recebidas por parâmetro
     * qual a célula que possui o identificador em questão.
     * Depois, a célula encontrada é salva na propriedade correspondente (@see IgnoredPropertiesMap).
     *
     * Mesmo que o mapa de células ainda não esteja completamente populado,
     * podemos contar com a presença das células que serão procuradas porque
     * é necessário que, na criação dos comandos, os elementos referenciados sejam sempre criados primeiro.
     *
     * @param cellMap mapa com as células já criadas
     * @param command comando de desenho a ser convertido
     *
     * @returns T onde T é um {@link VertexParameters} ou um {@link EdgeParameters}
     */
    private convertToMaxGraphParameters<T>(cellMap: Map<Identifier, Cell>, command: AnyDrawCommand): T {
        const result = { id: command.id, ...command.parameters } as unknown;

        for (const property of IGNORED_PROPERTIES_MAP[command.kind]) {
            if (command.parameters[property]) {
                result[property] = cellMap.get(command.parameters[property]);
            }
        }

        return result as T;
    }

    /**
     * Configura a animação dos elementos.
     *
     * @param graph gráfico do MaxGraph.
     */
    handleAnimations(graph: Graph) {
        for (const edge of this.edgeCells) {
            if ((edge as any).hasAnimation) {
                const state = graph.view.getState(edge);
                const pathElement = state?.shape?.node.getElementsByTagName("path");

                if (pathElement) {
                    // A posição 0 do pathElement tem o background da aresta, e a posição 1 tem a aresta em si.
                    const edgeElement = pathElement[1];

                    edgeElement.setAttribute("class", "flow-dash");
                }
            }
        }
    }
}
